import React, { useState, useEffect } from "react";
import { Container, Row, Col, Carousel, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosConfig";
import { FaArrowAltCircleLeft, FaArrowAltCircleRight } from "react-icons/fa";
import { buttonTextMap } from "../widget/translationMap"; // mapping file
import { useTranslation } from "react-i18next";
import { t } from "i18next";

const CACHE_KEY = "cached_banners";

// Utility function for formatting banner images
const formatBannerImages = (banners) => banners;
const areBannersEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Loading Spinner
const LoadingSpinner = () => (
  <Container className="text-center mt-top-cartpage-new">
    <Spinner animation="border" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </Container>
);

// Error Message
const ErrorMessage = ({ message, onRetry }) => (
  <Container className="mt-top-cartpage-new">
    <Alert variant="danger">
      <p>{message}</p>
      <p>{t('Please check your network connection')}</p>
      {onRetry && (
        <button onClick={onRetry} className="btn btn-primary">
          {t('Retry')}
        </button>
      )}
    </Alert>
  </Container>
);

const NoDataMessage = () => (
  <Container className="mt-top-cartpage-new">
    <Alert variant="info">{t('No banners available')}</Alert>
  </Container>
);

const BannerCarousel = () => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axiosInstance.get("/banners", { timeout: 10000 });
        const data = response.data;

        if (!data || !Array.isArray(data)) {
          throw new Error("Invalid banner data received");
        }

        const formatted = formatBannerImages(data);
        const cached = localStorage.getItem(CACHE_KEY);
        const cachedParsed = cached ? JSON.parse(cached) : [];

        if (!areBannersEqual(formatted, cachedParsed)) {
          localStorage.setItem(CACHE_KEY, JSON.stringify(formatted));
          setBanners(formatted);
        }

        // Clear any previous error messages on successful fetch
        setErrorMessage(null);
      } catch (error) {
        console.error("Banner Fetch Error:", error);

        // Use cached data if available, don't show error for slow internet
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const cachedBanners = JSON.parse(cached);
          setBanners(cachedBanners);
          // Don't set error message - we have cached data to show
        } else {
          // Only show error if no cached data at all
          setErrorMessage(
            error.response?.data?.message ||
            error.message ||
            "Failed to fetch banners"
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Load cached data immediately - this ensures instant display
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const cachedBanners = JSON.parse(cached);
      setBanners(cachedBanners);
      setIsLoading(false); // Stop loading since we have cached data
    }
    // Fetch latest data in background - this won't affect the display
    fetchBanners();
  }, []);

  // Background refresh useEffect
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      // Refresh banners every 5 minutes in background
      const fetchLatestBanners = async () => {
        try {
          const response = await axiosInstance.get("/banners", { timeout: 10000 });
          const data = response.data;

          if (data && Array.isArray(data)) {
            const formatted = formatBannerImages(data);
            const cached = localStorage.getItem(CACHE_KEY);
            const cachedParsed = cached ? JSON.parse(cached) : [];

            if (!areBannersEqual(formatted, cachedParsed)) {
              localStorage.setItem(CACHE_KEY, JSON.stringify(formatted));
              setBanners(formatted);
            }
          }
        } catch (error) {
          console.error("Background banner refresh failed:", error);
          // Silent fail for background refresh
        }
      };

      fetchLatestBanners();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (banners.length > 0) {
      const timer = setInterval(() => {
        setIndex((prev) => (prev < banners.length - 1 ? prev + 1 : 0));
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [banners]);

  const handleSelect = (selectedIndex) => setIndex(selectedIndex);

  const handleRetry = () => {
    setIsLoading(true);
    setErrorMessage(null);

    // Re-fetch banners manually
    const fetchBannersRetry = async () => {
      try {
        const response = await axiosInstance.get("/banners", { timeout: 10000 });
        const data = response.data;

        if (!data || !Array.isArray(data)) {
          throw new Error("Invalid banner data received");
        }

        const formatted = formatBannerImages(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(formatted));
        setBanners(formatted);
        setErrorMessage(null);
      } catch (error) {
        console.error("Banner Retry Error:", error);
        setErrorMessage("Failed to fetch banners. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBannersRetry();
  };

  // Only show loading if we have NO cached data AND still loading
  if (isLoading && banners.length === 0) return <LoadingSpinner />;
  if (errorMessage && banners.length === 0) return <ErrorMessage message={errorMessage} onRetry={handleRetry} />;
  if (banners.length === 0) return <NoDataMessage />;

  return (
    <section className="hero-banner-section mb-0" style={{ zIndex: 1, position: "relative", fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <Carousel
        activeIndex={index}
        onSelect={handleSelect}
        controls={false}  // Changed from true to false to remove navigation buttons
        indicators={true}
        interval={5000}
        pause={false}
        className="hero-banner overflow-hidden"
      >
        {banners.map((item, idx) => {
          // Map API text -> translation key
          const translationKey = buttonTextMap[item.buttonText] || item.buttonText;

          return (
            <Carousel.Item key={item._id || idx}>
              <Container className="py-0 py-md-5 px-md-5">
                <Row className="d-flex justify-content-center align-items-center">
                  <Col
                    xs={6}
                    className="d-flex flex-column justify-content-center align-items-start"
                  >
                    <div className="lh-sm text-color d-flex justify-content-center align-items-start flex-column text-banner-color">
                      <h1 className="banner-title mt-md-0 text-white">{item.title1}</h1>
                      <h1 className="banner-title mt-2 text-white">{item.title2}</h1>
                    </div>
                    <button
                      className="mt-md-5 mt-4 text-white banner-button px-md-4 px-3 btn-card-hover py-2 border-0 rounded-1 bg-danger banner-title-btn"
                      onClick={() => navigate("/men-perfume")}
                    >
                      {t(translationKey)}
                    </button>
                  </Col>
                  <Col xs={6} className="pe-0 pe-md-5 banner-poster">
                    <img
                      src={item.image}
                      alt={`Poster Banner ${idx + 1}`}
                      className="poster-img img-fluid"
                    />
                  </Col>
                </Row>
              </Container>
            </Carousel.Item>
          );
        })}
      </Carousel>
    </section>
  );
};

export default BannerCarousel;